/**
 * Splitting one release-note bullet into the line you read while scanning and
 * the explanation you open when it matters.
 *
 * Release bodies run to several thousand characters, so What's new showed a
 * wall of prose the moment you opened a release. Shortening them by hand was
 * never an option: there are 23 releases and 212 bullets, all already written
 * and all still true. So the short form is DERIVED, and it can be because the
 * changelog has always been written to one shape — `**Headline.** Then the
 * explanation.` — which 193 of those 212 bullets follow.
 *
 * Pure and dependency-free so it can be tested against the real CHANGELOG.md
 * rather than against examples chosen to make it pass.
 */

export interface SplitItem {
  /** The scannable line. Never empty. Bold markers stripped — it IS the lead. */
  headline: string;
  /** Everything after it, or null when the bullet is a one-liner. */
  detail: string | null;
}

/** `**Lead.** rest` — a bold run at the very start, which is the convention. */
const BOLD_LEAD = /^\*\*(.+?)\*\*\s*(.*)$/s;

/**
 * Does what follows a leading bold run START A NEW SENTENCE?
 *
 * Not every bold run at position 0 is a headline. The changelog also opens
 * sentences with an emphasised clause — "**Logged time … stops after May**, so
 * nearly every report reads as having logged none" — and splitting there gives
 * a headline with no full stop and a detail beginning ", so". So a lead only
 * counts when the remainder is empty or genuinely starts again: anything
 * beginning lowercase or with a comma is the same sentence carrying on.
 */
const CONTINUES_SENTENCE = /^[a-z,;:)\]]/;

/**
 * First sentence, for the minority of bullets with no bold lead.
 *
 * Requires whitespace after the stop so version numbers and "e.g." don't split
 * a sentence in the middle, and demands something before it so a bullet that
 * opens with an abbreviation doesn't yield a two-character headline.
 */
const FIRST_SENTENCE = /^(.{12,}?[.!?])\s+(\S.*)$/s;

function tidy(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export function splitItem(text: string): SplitItem {
  const source = tidy(text);

  const bold = BOLD_LEAD.exec(source);
  if (bold?.[1] !== undefined) {
    const headline = tidy(bold[1]);
    const detail = tidy(bold[2] ?? "");
    // A bold lead with nothing after it is the whole bullet.
    if (headline !== "" && !CONTINUES_SENTENCE.test(detail)) {
      return { headline, detail: detail === "" ? null : detail };
    }
  }

  const sentence = FIRST_SENTENCE.exec(source);
  if (sentence?.[1] !== undefined && sentence[2] !== undefined) {
    return { headline: tidy(sentence[1]), detail: tidy(sentence[2]) };
  }

  // One short line, or prose with no sentence break — nothing to hide.
  return { headline: source, detail: null };
}
